/**
 * swAuthBridge — the only way the service worker can learn who is signed in.
 *
 * A service worker has no localStorage, so `public/service-worker.js` could
 * only ever send the session cookie. That is not a credential for the six
 * widest-access users: their `aone_sso` is over the browser's 4096-byte limit
 * and the browser silently drops it (incident 2026-08-26). The one thing the
 * SW does on its own — re-subscribing push on `pushsubscriptionchange`, which
 * fires when the browser rotates the subscription and typically when no tab is
 * open to ask — therefore 401'd for exactly those users, and their push stayed
 * dead until they next opened the app.
 *
 * IndexedDB is the one store both a window and a worker can read, so the page
 * mirrors the JWT into it whenever the token is stored or cleared. This is not
 * a new exposure: the token already lives in localStorage, and both are
 * same-origin script-readable. It is cleared on logout with the same call.
 *
 * `public/service-worker.js` re-declares these three names (a classic worker
 * script cannot import a module) — keep them in step.
 */
export const SW_AUTH_DB    = 'aone-auth';
export const SW_AUTH_STORE = 'kv';
export const SW_AUTH_KEY   = 'token';

// Everything here is best-effort. Private windows, blocked site data and
// Safari's storage eviction all make IndexedDB throw or hang; none of that
// should ever break signing in, so every path resolves rather than rejects.
function withStore(mode, fn) {
  return new Promise((resolve) => {
    let db;
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB) return resolve(undefined);
      const open = indexedDB.open(SW_AUTH_DB, 1);
      open.onupgradeneeded = () => {
        if (!open.result.objectStoreNames.contains(SW_AUTH_STORE)) {
          open.result.createObjectStore(SW_AUTH_STORE);
        }
      };
      open.onerror = () => resolve(undefined);
      open.onblocked = () => resolve(undefined);
      open.onsuccess = () => {
        db = open.result;
        try {
          const tx = db.transaction(SW_AUTH_STORE, mode);
          const req = fn(tx.objectStore(SW_AUTH_STORE));
          tx.oncomplete = () => { resolve(req ? req.result : undefined); db.close(); };
          tx.onerror = () => { resolve(undefined); db.close(); };
          tx.onabort = () => { resolve(undefined); db.close(); };
        } catch { resolve(undefined); try { db.close(); } catch { /* already gone */ } }
      };
    } catch { resolve(undefined); }
  });
}

/** Mirror the JWT where the service worker can read it. */
export function mirrorAuthTokenToSW(token) {
  if (!token) return Promise.resolve();
  return withStore('readwrite', (store) => store.put(token, SW_AUTH_KEY));
}

/** Drop it — called from clearAuthToken, so logout clears both copies. */
export function clearAuthTokenForSW() {
  return withStore('readwrite', (store) => store.delete(SW_AUTH_KEY));
}

/** Read it back (used by the tests; the SW has its own copy of this logic). */
export function readBridgedAuthToken() {
  return withStore('readonly', (store) => store.get(SW_AUTH_KEY));
}
